import React, { useState } from 'react';
import './TermsOfUse.css';

const TermsOfUse: React.FC = () => {
  const [activeSection, setActiveSection] = useState<string | null>(null);

  const toggleSection = (section: string) => {
    setActiveSection(activeSection === section ? null : section);
  };

  const lastUpdated = "December 15, 2024";

  return (
    <div className="terms-of-use-container">
      <div className="terms-header">
        <div className="header-content">
          <h1>Terms of Use</h1>
          <p className="last-updated">Last updated: {lastUpdated}</p>
        </div>
      </div>

      <div className="terms-content">
        <div className="terms-intro">
          <p>
            Welcome to Sportagram! These Terms of Use ("Terms") govern your use of the Sportagram 
            platform, website, and mobile applications (collectively, the "Service") operated by 
            Sportagram Inc. ("we," "us," or "our").
          </p>
          <p>
            By accessing or using our Service, you agree to be bound by these Terms. If you disagree 
            with any part of these terms, then you may not access the Service.
          </p>
        </div>

        <div className="terms-sections">
          <div className="section">
            <div 
              className="section-header"
              onClick={() => toggleSection('acceptance')}
            >
              <h2>1. Acceptance of Terms</h2>
              <span className={`toggle-icon ${activeSection === 'acceptance' ? 'active' : ''}`}>
                ▼
              </span>
            </div>
            {activeSection === 'acceptance' && (
              <div className="section-content">
                <p>
                  By creating an account or using Sportagram, you acknowledge that you have read, 
                  understood, and agree to be bound by these Terms and our Privacy Policy. You must 
                  be at least 13 years old to use our Service.
                </p>
                <p>
                  If you are under 18, you represent that you have your parent's or guardian's 
                  permission to use the Service and that they have agreed to these Terms on your behalf.
                </p>
              </div>
            )}
          </div>

          <div className="section">
            <div 
              className="section-header"
              onClick={() => toggleSection('account')}
            >
              <h2>2. Account Registration</h2>
              <span className={`toggle-icon ${activeSection === 'account' ? 'active' : ''}`}>
                ▼
              </span>
            </div>
            {activeSection === 'account' && (
              <div className="section-content">
                <p>
                  To access certain features of the Service, you must register for an account. 
                  You agree to provide accurate, current, and complete information during registration.
                </p>
                <ul>
                  <li>You are responsible for maintaining the confidentiality of your account credentials</li>
                  <li>You must notify us immediately of any unauthorized use of your account</li>
                  <li>You may not transfer your account to another person</li>
                  <li>One person may not maintain more than one account</li>
                </ul>
              </div>
            )}
          </div>

          <div className="section">
            <div 
              className="section-header"
              onClick={() => toggleSection('conduct')}
            >
              <h2>3. User Conduct</h2>
              <span className={`toggle-icon ${activeSection === 'conduct' ? 'active' : ''}`}>
                ▼
              </span>
            </div>
            {activeSection === 'conduct' && (
              <div className="section-content">
                <p>You agree not to use the Service to:</p>
                <ul>
                  <li>Post content that is illegal, harmful, threatening, abusive, or discriminatory</li>
                  <li>Impersonate any person or entity or misrepresent your affiliation</li>
                  <li>Upload viruses or other malicious code</li>
                  <li>Spam, harass, or abuse other users</li>
                  <li>Violate any applicable laws or regulations</li>
                  <li>Infringe on intellectual property rights</li>
                  <li>Attempt to gain unauthorized access to our systems</li>
                </ul>
              </div>
            )}
          </div>

          <div className="section">
            <div 
              className="section-header"
              onClick={() => toggleSection('content')}
            >
              <h2>4. Content and Intellectual Property</h2>
              <span className={`toggle-icon ${activeSection === 'content' ? 'active' : ''}`}>
                ▼
              </span>
            </div>
            {activeSection === 'content' && (
              <div className="section-content">
                <p>
                  You retain ownership of content you post on Sportagram. However, by posting content, 
                  you grant us a worldwide, non-exclusive, royalty-free license to use, display, 
                  reproduce, and distribute your content in connection with the Service.
                </p>
                <p>
                  The Sportagram platform, including its design, features, and functionality, 
                  is owned by us and protected by copyright, trademark, and other intellectual property laws.
                </p>
              </div>
            )}
          </div>

          <div className="section">
            <div 
              className="section-header"
              onClick={() => toggleSection('privacy')}
            >
              <h2>5. Privacy and Data Protection</h2>
              <span className={`toggle-icon ${activeSection === 'privacy' ? 'active' : ''}`}>
                ▼
              </span>
            </div>
            {activeSection === 'privacy' && (
              <div className="section-content">
                <p>
                  Your privacy is important to us. Our Privacy Policy explains how we collect, 
                  use, and protect your information when you use our Service. By using Sportagram, 
                  you consent to the collection and use of information as outlined in our Privacy Policy.
                </p>
                <p>
                  We implement appropriate security measures to protect your personal information 
                  against unauthorized access, alteration, disclosure, or destruction.
                </p>
              </div>
            )}
          </div>

          <div className="section">
            <div 
              className="section-header"
              onClick={() => toggleSection('termination')}
            >
              <h2>6. Termination</h2>
              <span className={`toggle-icon ${activeSection === 'termination' ? 'active' : ''}`}>
                ▼
              </span>
            </div>
            {activeSection === 'termination' && (
              <div className="section-content">
                <p>
                  You may terminate your account at any time by contacting us or using the 
                  account deletion feature in your settings.
                </p>
                <p>
                  We may terminate or suspend your account immediately, without prior notice, 
                  if you breach these Terms or engage in conduct that we determine is harmful 
                  to other users or our Service.
                </p>
              </div>
            )}
          </div>

          <div className="section">
            <div 
              className="section-header"
              onClick={() => toggleSection('disclaimer')}
            >
              <h2>7. Disclaimers and Limitation of Liability</h2>
              <span className={`toggle-icon ${activeSection === 'disclaimer' ? 'active' : ''}`}>
                ▼
              </span>
            </div>
            {activeSection === 'disclaimer' && (
              <div className="section-content">
                <p>
                  The Service is provided "as is" without warranties of any kind. We do not 
                  guarantee that the Service will be uninterrupted, secure, or error-free.
                </p>
                <p>
                  To the fullest extent permitted by law, we shall not be liable for any 
                  indirect, incidental, special, consequential, or punitive damages arising 
                  from your use of the Service.
                </p>
              </div>
            )}
          </div>

          <div className="section">
            <div 
              className="section-header"
              onClick={() => toggleSection('changes')}
            >
              <h2>8. Changes to Terms</h2>
              <span className={`toggle-icon ${activeSection === 'changes' ? 'active' : ''}`}>
                ▼
              </span>
            </div>
            {activeSection === 'changes' && (
              <div className="section-content">
                <p>
                  We reserve the right to modify these Terms at any time. We will notify users 
                  of significant changes via email or through the Service. Your continued use 
                  of the Service after changes constitutes acceptance of the new Terms.
                </p>
              </div>
            )}
          </div>
        </div>

        <div className="contact-section">
          <h3>Contact Information</h3>
          <p>
            If you have any questions about these Terms of Use, please contact us at:
          </p>
          <div className="contact-details">
            <p><strong>Email:</strong> legal@sportagram.com</p>
            <p><strong>Address:</strong> Sportagram Inc., 123 Sports Avenue, Athletic City, AC 12345</p>
            <p><strong>Phone:</strong> +1 (555) 123-SPORT</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsOfUse;